<template>
  <div
    ref="root"
    :class="{
      'active-season-picker': true,
      'active-season-picker--open': isOpen,
      'active-season-picker--filled': season !== null,
    }"
    title="Active season"
    @click="onClick"
  >
    <span class="active-season-picker__label">Season</span>
    <!-- Icon and pill are BOTH always mounted; the --filled modifier cross-
         fades them (no Vue transition hooks, which get lost when the popper
         closes mid-swap and would leave the slot empty). The pill keeps
         rendering the last season after one is cleared, so it collapses with
         the chip instead of disappearing the instant the model empties. -->
    <span
      :class="{
        'active-season-picker__value': true,
        'active-season-picker__value--filled': season !== null,
      }"
      :style="{ '--season-pill-width': pillWidth ? `${pillWidth}px` : undefined }"
    >
      <span class="active-season-picker__icon" v-html="seasonIcon" />
      <span class="active-season-picker__pill-wrap">
        <span ref="pill" class="active-season-picker__pill">
          <fs-pill-season v-if="lastSeason" :season="lastSeason" />
        </span>
      </span>
    </span>
    <dropdown-caret class="active-season-picker__caret" :is-flipped="isOpen" />
  </div>

  <fs-popper
    ref="popper"
    animation="none"
    :offset="[0, 12]"
    :close-on-outside-click-check="(target: Node) => rootRef?.contains(target)"
    is-close-on-esc
    no-styling
    @close="isOpen = false"
  >
    <div class="active-season-picker__popper">
      <fs-input
        ref="input"
        v-model="searchQuery"
        class="active-season-picker__search"
        is-search
        placeholder="Find season"
      />
      <select-seasons
        selected-label="Active season"
        :search-query="searchQuery"
        :selected-seasons="season ? [season] : []"
        :available-seasons="seasons.filter((s) => s.id !== seasonId)"
        :can-add-new-season="false"
        @add-season="setSeason"
        @remove-season="setSeason(null)"
      />
    </div>
  </fs-popper>
</template>

<script setup lang="ts">
import type { SeasonDto } from '@/ApiClient';
import FsPillSeason from '@components/pills/fs-pill-season.vue';
import FsPopper from '@components/poppers/fs-popper.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import SelectSeasons from '@components/select-seasons/select-seasons.vue';
import FsInput from '@components/form-elements/fs-input.vue';
import DropdownCaret from '@components/dropdown-caret.vue';
import seasonIcon from '@icons/filters-icons/seasons.svg';

const seasonId = defineModel<number | null>({ required: true });

const { seasons } = defineProps<{ seasons: SeasonDto[] }>();

const rootRef = useTemplateRef<any>('root');
const inputRef = useTemplateRef<any>('input');
const popperRef = useTemplateRef<any>('popper');
const isOpen = ref<boolean>(false);
const searchQuery = ref('');

const season = computed(() => seasons.find((s) => s.id === seasonId.value) ?? null);

// The pill renders from this rather than from `season`, so clearing the season
// leaves it mounted and it can collapse away instead of vanishing in one frame.
const lastSeason = ref<SeasonDto | null>(season.value);
watch(season, (value) => {
  if (value) lastSeason.value = value;
});

// The chip animates to the pill's real width. Interpolating to a guessed
// max-width instead leaves dead time at one end of the tween — the pill is
// narrower than the guess, so nothing moves until the value passes its width,
// and then it snaps.
const pillRef = useTemplateRef<HTMLElement>('pill');
const pillWidth = ref(0);
let pillObserver: ResizeObserver | undefined;

onMounted(() => {
  if (!pillRef.value) return;

  // Measured up front so the first swap already tweens to the right width, then
  // observed because it changes with the season name and again once the web
  // font settles.
  pillWidth.value = pillRef.value.getBoundingClientRect().width;

  pillObserver = new ResizeObserver(([entry]) => {
    pillWidth.value = entry.contentRect.width;
  });
  pillObserver.observe(pillRef.value);
});

onBeforeUnmount(() => pillObserver?.disconnect());

watch(isOpen, () => (searchQuery.value = ''));

async function onClick() {
  if (isOpen.value) {
    popperRef.value?.close();
    return;
  }

  isOpen.value = true;
  await nextTick();

  await popperRef.value?.open({ target: rootRef.value });
  inputRef.value?.focus();
}

async function setSeason(id: number | null) {
  seasonId.value = id;
  popperRef.value?.close();
}
</script>

<style scoped lang="scss">
@import '@styles/variables';

// The picker sits on the refresh's light navbar, so it reads as a surface chip
// on a hairline — not the white-on-dark outline the picker carries elsewhere.
$picker-transition: 0.25s cubic-bezier(0.4, 0, 0.2, 1);

.active-season-picker {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.7rem;
  cursor: pointer;
  padding: 0.5rem 0.9rem 0.5rem 1.1rem;
  box-sizing: border-box;
  border: 1px solid $c-hairline-strong;
  border-radius: $br-pill;
  background: $c-surface;
  transition:
    background-color $picker-transition,
    border-color $picker-transition;

  &:hover {
    background: $c-surface-muted;
    border-color: $c-ink;
  }

  &--open {
    background: $c-surface-muted;
    border-color: $c-ink;
  }

  // A selected season makes the picker read as "set": ink border and ink label
  // instead of the muted resting greys.
  &--filled {
    border-color: $c-ink;

    .active-season-picker__label {
      color: $c-ink;
    }
  }

  &__label {
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba($c-ink, 0.4);
    transition: color $picker-transition;
  }

  // Compact when no season is selected (just the icon, no reserved white
  // space); stretches to fit the season pill when one is picked. Both children
  // are always mounted, so the slot can never end up empty mid-swap.
  //
  // Each side declares the timing for the transition *into* it, which is what
  // lets clearing and picking a season read differently: on the way out the
  // pill's text fades before the width closes, so it is never seen squashing.
  &__value {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-width: 0;

    &--filled {
      .active-season-picker__icon {
        max-width: 0;
        opacity: 0;
        transition:
          max-width $picker-transition,
          opacity 0.1s ease;
      }

      .active-season-picker__pill-wrap {
        max-width: var(--season-pill-width, 9rem);
        opacity: 1;
        transition:
          max-width $picker-transition,
          opacity 0.16s ease 0.08s;
      }
    }
  }

  &__pill-wrap {
    display: flex;
    align-items: center;
    max-width: 0;
    opacity: 0;
    overflow: hidden;
    transition:
      max-width $picker-transition,
      opacity 0.12s ease;
  }

  // Holds its natural width so the wrapper clips it rather than squeezing it,
  // and so its measured width drives the tween.
  &__pill {
    display: flex;
    align-items: center;
    flex: none;
  }

  &__icon {
    display: flex;
    align-items: center;
    max-width: 1.8rem;
    opacity: 1;
    overflow: hidden;
    transition:
      max-width $picker-transition,
      opacity 0.16s ease 0.09s;

    :deep(svg) {
      width: 1.8rem;
      height: 1.8rem;
      flex: none;
      fill: $c-ink-soft;
    }
  }

  &__caret {
    :deep(svg) {
      fill: $c-ink-soft;
    }
  }

  &__search {
    width: 100%;
    margin-bottom: 1.4rem;
  }

  &__popper {
    border-radius: $br-card;
    background: $c-surface;
    box-shadow: $shadow-lifted;
    width: 24rem;
    padding: 2rem 2rem 1.6rem;
  }
}
</style>
